import { Box, Grid2 } from '@mui/material';
import React, { useState, useEffect } from 'react';
import PatientDetails from './PatientDetails';
import FacilityDetails from './FacilityDetails';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useAssigneeChangeAlert,
  useGetConsultMoreDetails,
} from '../../API/hooks/consultMoreDetailsHook';
import ChatQLER from '@qler-view/shared_lib/src/shared/Chat/ChatQLER';
import {
  useGetAcsChatToken,
  useGetConsultChatThread,
} from '../../API/hooks/acsChatHook';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src/shared/constants/portalType';
import { CallStatusEnum } from '../myconsults/shared/CallStatusEnum';
import WarningDialog from '../components/AcceptWarningDialog';

const MoreDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [consultId, setConsultId] = useState(location.state?.consultId ?? 0);

  const [assignedFlag, setAssignedFlag] = useState(
    location.state?.assignedFlag
  );
  const userId = Number.parseInt(localStorage.getItem('userId') ?? '0');
  const { consultMoreDetails, refetch } = useGetConsultMoreDetails(
    userId,
    consultId
  );

  const [isInitialFetchDone, setIsInitialFetchDone] = useState(false);
  const [intervalId, setIntervalId] = useState<number | null>(null);

  const { assigneeChangeAlertData, refetch: refetchAssigneeAlert } =
    useAssigneeChangeAlert(userId, consultId);

  const [isChatOpen, setIsChatOpen] = useState(false);

  const { acsChatToken } = useGetAcsChatToken(userId, PORTAL_TYPES.CLINICAL);
  const { consultChatThread } = useGetConsultChatThread(
    consultId,
    userId,
    PORTAL_TYPES.CLINICAL,
    isChatOpen
  );

  const setViewOnly = (status?: string) => {
    const normalizedStatus = status?.toLowerCase();

    const isClosed =
      normalizedStatus === CallStatusEnum.Completed.toLowerCase() ||
      normalizedStatus === CallStatusEnum.Cancelled.toLowerCase() ||
      normalizedStatus === CallStatusEnum.New.toLowerCase() ||
      location.state?.assignedFlag === true;

    return isClosed;
  };

  useEffect(() => {
    const consultStatus = consultMoreDetails?.sessionStatus;
    if (
      consultStatus?.toLowerCase() === CallStatusEnum.Abandoned ||
      consultMoreDetails?.isFacilityChanged === true
    ) {
      navigate('/provider/consults', { replace: true });
    }
  }, [consultMoreDetails]);

  useEffect(() => {
    // #44250 - ipad issue 3 fix
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (location.state?.chatOpen) {
      setIsChatOpen(true);
    }
  }, [location.state?.chatOpen]);

  const handleChatToggle = () => {
    setIsChatOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!consultId) {
      navigate('/provider/consults', { replace: true });
    }
  }, [consultId, navigate]);

  useEffect(() => {
    setConsultId(location.state?.consultId ?? 0);
    setAssignedFlag(location.state?.assignedFlag ?? false);
  }, [location.state?.consultId, location.state?.assignedFlag]);

  useEffect(() => {
    if (!assignedFlag) {
      const interId = setInterval(() => {
        refetchAssigneeAlert().finally(() => {
          setIsInitialFetchDone(true);
        });
      }, 4000);
      setIntervalId(interId as unknown as number);
      return () => clearInterval(interId);
    }
  }, [assignedFlag]);

  const [alertAccept, setAlertAccept] = useState<{
    message: string;
    title: string;
  } | null>(null);

  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (isInitialFetchDone) {
      if (
        assigneeChangeAlertData?.assignedToAnotherProvider === true &&
        assignedFlag === false
      ) {
        setAlertAccept({
          title: 'Alert',
          message: `This consult request has already been assigned to another provider`,
        });
        setShowDialog(true);
        if (intervalId) {
          clearInterval(intervalId);
        }
      } else {
        if (assigneeChangeAlertData?.onHold) {
          navigate('/provider/consults', { replace: true });
        }
        setShowDialog(false);
      }
    }
  }, [assigneeChangeAlertData, assignedFlag, isInitialFetchDone]);

  return (
    <Box>
      <Grid2 container spacing={3} className="width-100">
        <Grid2
          size={{
            xs: 12,
            sm: 12,
            md: isChatOpen ? 8.5 : 12,
            lg: isChatOpen ? 8.5 : 12,
            xl: isChatOpen ? 8.5 : 12,
          }}
        >
          <Box>
            {consultMoreDetails && (
              <PatientDetails
                consultPatientDetails={consultMoreDetails}
                consultId={consultId}
                assignedFlag={assignedFlag}
                refetch={refetch}
                onChatToggle={handleChatToggle}
                unreadChatCount={consultMoreDetails?.unreadMessageCount}
              />
            )}
          </Box>
          <Box>
            {consultMoreDetails && (
              <FacilityDetails consultFacilityDetails={consultMoreDetails} />
            )}
          </Box>
        </Grid2>
        {showDialog && (
          <WarningDialog
            alertAccept={alertAccept}
            onClose={() => navigate('/provider/consults', { replace: true })}
            buttonText="Go back to consult dashboard"
          />
        )}
        <Grid2
          size={{
            xs: 12,
            sm: 12,
            md: isChatOpen ? 3.5 : 0,
            lg: isChatOpen ? 3.5 : 0,
            xl: isChatOpen ? 3.5 : 0,
          }}
        >
          <Box className="pb-24">
            <ChatQLER
              onClose={() => setIsChatOpen(false)}
              acsConfig={
                acsChatToken
                  ? {
                      endpoint: import.meta.env.VITE_ACS_ENDPOINT,
                      token: acsChatToken.token,
                      userId: acsChatToken.acsUserId,
                      displayName: localStorage.getItem('username') ?? 'User',
                      portalType: PORTAL_TYPES.CLINICAL,
                    }
                  : undefined
              }
              isViewOnly={setViewOnly(consultMoreDetails?.sessionStatus)}
              threadId={consultChatThread?.threadId}
              subtitle={consultChatThread?.participants || ''}
              hidden={!isChatOpen}
            />
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default MoreDetails;
